/* ============================================================================
   METAR — lectura y clasificación según la matriz FRAT

   POR QUÉ EXISTE

   El FRAT del operador tiene tres grupos de meteorología —salida, ruta,
   destino— y hasta ahora se contestaban a mano, eligiendo entre CAVOK,
   «1500' - 5KM» y «Sin información». En la práctica se elegía «Sin
   información», que es la opción MÁS PROBABLE de la matriz: nadie iba a
   transcribir un METAR a mano antes de cada vuelo.

   Acá se lee el METAR crudo y se clasifica en esas mismas tres categorías. No
   se inventa una escala nueva: los índices son los del formulario del
   operador, porque el FRAT ya está aprobado y cambiarlo es un trámite.

   LO QUE ESTE MÓDULO NO HACE

   No decide si se vuela. Traduce un texto a la casilla que corresponde y dice
   por qué. Un METAR de hace tres horas no es la meteorología de ahora, y eso
   también se informa.

   Todo es puro: entra texto, sale un objeto. Se puede probar sin red.
   ========================================================================== */

/** Categorías del FRAT, en el mismo orden que las opciones del formulario. */
export const CATEGORIAS = ["CAVOK", "1500-5KM", "SIN-INFO"];

/** Índices del grupo de meteorología en el formulario del operador. */
export const INDICE_FRAT = { CAVOK: 0, "1500-5KM": 1, "SIN-INFO": 2 };

/** Umbrales de la categoría intermedia, tal como los nombra el formulario. */
export const UMBRALES = {
  techoPies: 1500,
  visibilidadMetros: 5000,
  /* Un METAR viejo deja de representar la meteorología de ahora. Media hora es
     el intervalo normal de emisión; una hora ya es otra situación. */
  frescoMinutos: 30,
  viejoMinutos: 75,
};

const num = (v) => (Number.isFinite(v) ? v : null);

/* ------------------------------------------------------------------ viento */

/**
 * El grupo de viento: `dddff(f)Gff(f)KT`, o `VRB` cuando es variable.
 *
 * `00000KT` es calma y NO es «sin dato»: son cosas distintas y el FRAT las
 * puntúa distinto.
 */
export function leerViento(texto) {
  const m = /\b(\d{3}|VRB)(\d{2,3})(?:G(\d{2,3}))?(KT|MPS|KMH)\b/.exec(texto || "");
  if (!m) return { direccionGrados: null, nudos: null, rafagaKt: null, variable: false };

  const factor = m[4] === "MPS" ? 1.94384 : (m[4] === "KMH" ? 0.539957 : 1);
  const redondear = (v) => (v === null ? null : Math.round(v * factor));

  return {
    direccionGrados: m[1] === "VRB" ? null : Number(m[1]),
    variable: m[1] === "VRB",
    nudos: redondear(Number(m[2])),
    rafagaKt: m[3] ? redondear(Number(m[3])) : null,
  };
}

/**
 * El viento que importa para el límite: el mayor entre sostenido y ráfaga.
 *
 * Un día de 20 kt con ráfagas de 35 no es un día de 20 kt. Es la misma regla
 * que ya usa el FRAT para derivar el grupo de viento.
 */
export function vientoParaElLimite(viento) {
  const v = num(viento?.nudos);
  const r = num(viento?.rafagaKt);
  if (v === null && r === null) return null;
  return Math.max(v ?? 0, r ?? 0);
}

/* -------------------------------------------------------------- visibilidad */

/** Visibilidad en metros. Acepta el formato en metros y el de millas. */
export function leerVisibilidadMetros(texto) {
  const t = texto || "";
  if (/\bCAVOK\b/.test(t)) return 10000;

  // Millas terrestres: `10SM`, `1 1/2SM`, `M1/4SM`.
  const millas = /\b(M)?(\d+)?\s?(?:(\d)\/(\d))?SM\b/.exec(t);
  if (millas && (millas[2] || millas[3])) {
    const entero = millas[2] ? Number(millas[2]) : 0;
    const fraccion = millas[3] ? Number(millas[3]) / Number(millas[4]) : 0;
    const total = (entero + fraccion) * 1609.34;
    // `M` significa «menos de»: se toma el valor como tope, no como medida.
    return Math.round(millas[1] ? Math.max(total - 1, 0) : total);
  }

  /* Metros: cuatro dígitos sueltos. `9999` significa 10 km o más.
     Se excluye el grupo de viento y el de temperatura para no confundirlos. */
  const metros = /(?:^|\s)(\d{4})(?:NDV)?(?=\s|$)/.exec(t.replace(/\b\d{3}\d{2,3}(?:G\d{2,3})?(?:KT|MPS|KMH)\b/g, " "));
  if (metros) {
    const v = Number(metros[1]);
    return v === 9999 ? 10000 : v;
  }
  return null;
}

/* -------------------------------------------------------------------- techo */

/**
 * El techo: la base de la primera capa BKN u OVC, en pies.
 *
 * FEW y SCT no son techo — no cubren el cielo. Contarlas como techo haría
 * rechazar días perfectamente volables.
 */
export function leerTechoPies(texto) {
  const t = texto || "";
  if (/\bCAVOK\b/.test(t) || /\bNSC\b/.test(t) || /\bNCD\b/.test(t)) return 99999;

  // Cielo vertical: es peor que cualquier techo.
  const vv = /\bVV(\d{3})\b/.exec(t);
  if (vv) return Number(vv[1]) * 100;

  let menor = null;
  const capas = t.matchAll(/\b(BKN|OVC)(\d{3})\b/g);
  for (const c of capas) {
    const pies = Number(c[2]) * 100;
    if (menor === null || pies < menor) menor = pies;
  }
  if (menor !== null) return menor;

  // Solo FEW/SCT: no hay techo, el cielo está abierto.
  if (/\b(FEW|SCT)\d{3}\b/.test(t)) return 99999;
  if (/\bSKC\b|\bCLR\b/.test(t)) return 99999;
  return null;
}

/* --------------------------------------------------- fenómenos significativos */

/**
 * Fenómenos que cambian la decisión aunque el techo y la visibilidad den bien.
 *
 * Una tormenta con CAVOK alrededor sigue siendo una tormenta.
 */
export const FENOMENOS_GRAVES = [
  ["TS", "tormenta"],
  ["GR", "granizo"],
  ["FZ", "engelamiento"],
  ["SQ", "turbonada"],
  ["FC", "tromba"],
  ["SS", "tormenta de arena"],
  ["DS", "tormenta de polvo"],
  ["VA", "ceniza volcánica"],
];

export function leerFenomenos(texto) {
  const t = texto || "";
  const hallados = [];
  for (const [codigo, nombre] of FENOMENOS_GRAVES) {
    // El código va pegado a la intensidad (`+TSRA`, `-FZRA`) o solo.
    if (new RegExp(`\\b[-+]?[A-Z]{0,2}${codigo}[A-Z]{0,4}\\b`).test(t)) hallados.push(nombre);
  }
  return hallados;
}

/* ----------------------------------------------------------------- cabecera */

/** OACI y hora de emisión. */
export function leerCabecera(texto) {
  const t = (texto || "").trim();
  const oaci = /\b([A-Z]{4})\b/.exec(t.replace(/^METAR\s+|^SPECI\s+/, ""));
  const hora = /\b(\d{2})(\d{2})(\d{2})Z\b/.exec(t);
  return {
    oaci: oaci ? oaci[1] : null,
    dia: hora ? Number(hora[1]) : null,
    horaUTC: hora ? `${hora[2]}:${hora[3]}` : null,
    esSpeci: /^SPECI\b/.test(t),
  };
}

/**
 * Antigüedad del informe en minutos.
 *
 * El METAR trae día y hora UTC, sin mes: se resuelve contra la fecha actual,
 * contemplando el cambio de mes. Sin esto, un informe del día 1 leído el día 31
 * daría treinta días de antigüedad.
 */
export function antiguedadMinutos(cabecera, ahora = new Date()) {
  if (!cabecera?.dia || !cabecera?.horaUTC) return null;
  const [hh, mm] = cabecera.horaUTC.split(":").map(Number);

  /* Se prueba el mes actual y el anterior, y se elige el que dé una
     antigüedad razonable.

     `setUTCMonth(-1)` sobre un día 31 no sirve: si el mes anterior tiene 30
     días, JavaScript lo desborda al 1 del mes siguiente y vuelve al punto de
     partida. Construir la fecha desde cero con el mes correcto lo evita. */
  const candidatos = [0, -1].map((desplazamiento) => Date.UTC(
    ahora.getUTCFullYear(), ahora.getUTCMonth() + desplazamiento, cabecera.dia, hh, mm,
  ));

  for (const emitido of candidatos) {
    const minutos = Math.round((ahora.getTime() - emitido) / 60000);
    /* Una hora de margen hacia el futuro cubre relojes desfasados; más de dos
       días de antigüedad no es un METAR utilizable de todos modos. */
    if (minutos >= -60 && minutos <= 2 * 24 * 60) return Math.max(minutos, 0);
  }
  return null;
}

/* ------------------------------------------------------------------ lectura */

/**
 * Lee un METAR crudo.
 *
 * Devuelve siempre un objeto: los campos que no se pudieron leer van en
 * `null`. Nunca lanza — un METAR con un formato raro no puede tumbar la
 * pantalla de planificación.
 */
export function leerMetar(crudo, ahora = new Date()) {
  const texto = typeof crudo === "string" ? crudo.toUpperCase().trim() : "";
  if (!texto) {
    return {
      crudo: null, valido: false, oaci: null, horaUTC: null, antiguedadMin: null,
      viento: { direccionGrados: null, nudos: null, rafagaKt: null, variable: false },
      vientoLimiteKt: null, visibilidadM: null, techoFt: null, cavok: false,
      fenomenos: [], temperaturaC: null, rocioC: null, qnhHpa: null,
    };
  }

  const cabecera = leerCabecera(texto);
  const viento = leerViento(texto);

  const temp = /\b(M?\d{2})\/(M?\d{2})\b/.exec(texto);
  const aNumero = (v) => (v ? Number(v.replace("M", "-")) : null);

  const qnhQ = /\bQ(\d{4})\b/.exec(texto);
  const qnhA = /\bA(\d{4})\b/.exec(texto);

  return {
    crudo: texto,
    valido: Boolean(cabecera.oaci),
    oaci: cabecera.oaci,
    esSpeci: cabecera.esSpeci,
    horaUTC: cabecera.horaUTC,
    antiguedadMin: antiguedadMinutos(cabecera, ahora),
    viento,
    vientoLimiteKt: vientoParaElLimite(viento),
    visibilidadM: leerVisibilidadMetros(texto),
    techoFt: leerTechoPies(texto),
    cavok: /\bCAVOK\b/.test(texto),
    fenomenos: leerFenomenos(texto),
    temperaturaC: aNumero(temp?.[1]),
    rocioC: aNumero(temp?.[2]),
    // Pulgadas de mercurio a hectopascales, para que la app tenga una sola unidad.
    qnhHpa: qnhQ ? Number(qnhQ[1]) : (qnhA ? Math.round((Number(qnhA[1]) / 100) * 33.8639) : null),
  };
}

/* ------------------------------------------------------------ clasificación */

/**
 * Clasifica un METAR ya leído en las tres categorías del FRAT.
 *
 * @returns {{categoria: string, indiceFrat: number, motivo: string, avisos: string[]}}
 */
export function clasificarParaFrat(m, umbrales = UMBRALES) {
  const avisos = [];

  if (!m || !m.valido) {
    return {
      categoria: "SIN-INFO",
      indiceFrat: INDICE_FRAT["SIN-INFO"],
      motivo: "No hay METAR para este aeródromo.",
      avisos,
    };
  }

  /* Un informe viejo no es la meteorología de ahora. Se avisa, y pasado el
     límite se trata como sin información: es lo honesto. */
  if (Number.isFinite(m.antiguedadMin)) {
    if (m.antiguedadMin > umbrales.viejoMinutos) {
      return {
        categoria: "SIN-INFO",
        indiceFrat: INDICE_FRAT["SIN-INFO"],
        motivo: `El METAR es de hace ${m.antiguedadMin} min: ya no representa la meteorología actual.`,
        avisos,
      };
    }
    if (m.antiguedadMin > umbrales.frescoMinutos) {
      avisos.push(`El informe tiene ${m.antiguedadMin} min. Conviene comprobar si hay uno más nuevo.`);
    }
  }

  /* Los fenómenos mandan sobre el techo y la visibilidad. Una tormenta con
     CAVOK alrededor sigue siendo una tormenta, y el FRAT no tiene una casilla
     para eso: se baja a la peor categoría y se dice por qué. */
  if (m.fenomenos.length > 0) {
    return {
      categoria: "SIN-INFO",
      indiceFrat: INDICE_FRAT["SIN-INFO"],
      motivo: `Fenómeno significativo: ${m.fenomenos.join(", ")}. No entra en «CAVOK» ni en «1500' - 5 KM».`,
      avisos,
    };
  }

  const techo = m.techoFt;
  const vis = m.visibilidadM;

  if (techo === null || vis === null) {
    return {
      categoria: "SIN-INFO",
      indiceFrat: INDICE_FRAT["SIN-INFO"],
      motivo: "El METAR no trae techo o visibilidad legibles.",
      avisos,
    };
  }

  if (m.cavok) {
    return { categoria: "CAVOK", indiceFrat: INDICE_FRAT.CAVOK, motivo: "CAVOK informado.", avisos };
  }

  /* «CAVOK equivalente»: 10 km o más y sin nubes bajo 5000 ft. No se declara
     CAVOK a secas porque el METAR no lo dice; se clasifica igual y se explica. */
  if (vis >= 10000 && techo >= 5000) {
    return {
      categoria: "CAVOK",
      indiceFrat: INDICE_FRAT.CAVOK,
      motivo: `Visibilidad ${vis >= 10000 ? "10 km o más" : `${vis} m`} y techo ${techo >= 99999 ? "sin nubes bajas" : `${techo} ft`}: equivale a CAVOK.`,
      avisos,
    };
  }

  if (techo >= umbrales.techoPies && vis >= umbrales.visibilidadMetros) {
    return {
      categoria: "1500-5KM",
      indiceFrat: INDICE_FRAT["1500-5KM"],
      motivo: `Techo ${techo >= 99999 ? "sin nubes bajas" : `${techo} ft`} y visibilidad ${vis} m: sobre el mínimo de 1500 ft y 5 km.`,
      avisos,
    };
  }

  /* Por debajo del mínimo del formulario no hay casilla mejor que «Sin
     información», que es la que más puntúa. El motivo dice qué falló, para que
     el piloto no crea que es un problema de datos. */
  const faltas = [];
  if (techo < umbrales.techoPies) faltas.push(`techo ${techo} ft (mínimo ${umbrales.techoPies})`);
  if (vis < umbrales.visibilidadMetros) faltas.push(`visibilidad ${vis} m (mínimo ${umbrales.visibilidadMetros})`);

  return {
    categoria: "SIN-INFO",
    indiceFrat: INDICE_FRAT["SIN-INFO"],
    motivo: `Por debajo del mínimo del formulario: ${faltas.join(" y ")}.`,
    avisos,
    bajoMinimos: true,
  };
}

/**
 * Lee y clasifica de una sola vez, que es como lo usa la pantalla.
 */
export function analizarMetar(crudo, ahora = new Date(), umbrales = UMBRALES) {
  const lectura = leerMetar(crudo, ahora);
  return { ...lectura, ...clasificarParaFrat(lectura, umbrales) };
}
